import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DateInput, validateDateSubmission } from '../../../../shared/components/DateInput.js';

/* ─────────────────────────────────────────────────────────────────────────
   L17.06 — Config Engine (`/admin/master-config/engine`).
   Unified CRUD surface over the M17 configuration engine (§35 #7–#12):
   Approval Matrix, Workflow, SLA, Tax, Notification and System Parameter.
   Each domain supports list, create and deactivate over `v1/m17/engine/*`.
   ───────────────────────────────────────────────────────────────────────── */

type FieldType = 'text' | 'number' | 'date';

interface FieldSpec {
  readonly name: string;
  readonly label: string;
  readonly type: FieldType;
  readonly required?: boolean;
}

interface DomainSpec {
  readonly key: string;
  readonly label: string;
  readonly path: string; // engine sub-path (e.g. 'approval-matrix')
  readonly idField: string;
  readonly columns: readonly { readonly key: string; readonly label: string }[];
  readonly fields: readonly FieldSpec[];
  /** Builds the create body from raw form values, injecting fixed defaults. */
  readonly build: (v: Record<string, string>) => Record<string, unknown>;
}

const TODAY = (): string => new Date().toISOString().slice(0, 10);

const DOMAINS: readonly DomainSpec[] = [
  {
    key: 'approval-matrix',
    label: 'm17.configEngine.domApprovalMatrix',
    path: 'approval-matrix',
    idField: 'matrixId',
    columns: [
      { key: 'matrixCode', label: 'm17.configEngine.colCode' },
      { key: 'matrixName', label: 'm17.configEngine.colName' },
      { key: 'moduleCode', label: 'm17.configEngine.colModule' },
      { key: 'approvalType', label: 'm17.configEngine.colType' },
    ],
    fields: [
      { name: 'matrixCode', label: 'm17.configEngine.fMatrixCode', type: 'text', required: true },
      { name: 'matrixName', label: 'm17.configEngine.fMatrixName', type: 'text', required: true },
      { name: 'moduleCode', label: 'm17.configEngine.fModuleCode', type: 'text', required: true },
      { name: 'approvalType', label: 'm17.configEngine.fApprovalType', type: 'text', required: true },
    ],
    build: (v) => ({
      matrixCode: v.matrixCode,
      matrixName: v.matrixName,
      moduleCode: v.moduleCode,
      approvalType: v.approvalType,
    }),
  },
  {
    key: 'workflows',
    label: 'm17.configEngine.domWorkflow',
    path: 'workflows',
    idField: 'workflowId',
    columns: [
      { key: 'workflowCode', label: 'm17.configEngine.colCode' },
      { key: 'workflowName', label: 'm17.configEngine.colName' },
      { key: 'moduleCode', label: 'm17.configEngine.colModule' },
    ],
    fields: [
      { name: 'workflowCode', label: 'm17.configEngine.fWorkflowCode', type: 'text', required: true },
      { name: 'workflowName', label: 'm17.configEngine.fWorkflowName', type: 'text', required: true },
      { name: 'moduleCode', label: 'm17.configEngine.fModuleCode', type: 'text', required: true },
      { name: 'effectiveFromDate', label: 'm17.configEngine.fEffectiveFrom', type: 'date', required: true },
    ],
    build: (v) => ({
      workflowCode: v.workflowCode,
      workflowName: v.workflowName,
      moduleCode: v.moduleCode,
      stages: [],
      transitions: [],
      effectiveFromDate: v.effectiveFromDate || TODAY(),
    }),
  },
  {
    key: 'slas',
    label: 'm17.configEngine.domSla',
    path: 'slas',
    idField: 'slaId',
    columns: [
      { key: 'slaCode', label: 'm17.configEngine.colCode' },
      { key: 'slaName', label: 'm17.configEngine.colName' },
      { key: 'moduleCode', label: 'm17.configEngine.colModule' },
      { key: 'targetHours', label: 'm17.configEngine.colTargetHrs' },
    ],
    fields: [
      { name: 'slaCode', label: 'm17.configEngine.fSlaCode', type: 'text', required: true },
      { name: 'slaName', label: 'm17.configEngine.fSlaName', type: 'text', required: true },
      { name: 'slaType', label: 'm17.configEngine.fSlaType', type: 'text', required: true },
      { name: 'moduleCode', label: 'm17.configEngine.fModuleCode', type: 'text', required: true },
      { name: 'targetHours', label: 'm17.configEngine.fTargetHours', type: 'number', required: true },
      { name: 'effectiveFromDate', label: 'm17.configEngine.fEffectiveFrom', type: 'date', required: true },
    ],
    build: (v) => ({
      slaCode: v.slaCode,
      slaName: v.slaName,
      slaType: v.slaType,
      moduleCode: v.moduleCode,
      targetHours: Number(v.targetHours),
      effectiveFromDate: v.effectiveFromDate || TODAY(),
    }),
  },
  {
    key: 'taxes',
    label: 'm17.configEngine.domTax',
    path: 'taxes',
    idField: 'taxId',
    columns: [
      { key: 'taxCode', label: 'm17.configEngine.colCode' },
      { key: 'taxName', label: 'm17.configEngine.colName' },
      { key: 'taxType', label: 'm17.configEngine.colType' },
      { key: 'ratePercentage', label: 'm17.configEngine.colRatePct' },
    ],
    fields: [
      { name: 'taxCode', label: 'm17.configEngine.fTaxCode', type: 'text', required: true },
      { name: 'taxName', label: 'm17.configEngine.fTaxName', type: 'text', required: true },
      { name: 'taxType', label: 'm17.configEngine.fTaxType', type: 'text', required: true },
      { name: 'ratePercentage', label: 'm17.configEngine.fRatePercentage', type: 'number', required: true },
      { name: 'effectiveFromDate', label: 'm17.configEngine.fEffectiveFrom', type: 'date', required: true },
    ],
    build: (v) => ({
      taxCode: v.taxCode,
      taxName: v.taxName,
      taxType: v.taxType,
      ratePercentage: Number(v.ratePercentage),
      effectiveFromDate: v.effectiveFromDate || TODAY(),
    }),
  },
  {
    key: 'payment-configs',
    label: 'm17.configEngine.domPayment',
    path: 'payment-configs',
    idField: 'configId',
    columns: [
      { key: 'configCode', label: 'm17.configEngine.colCode' },
      { key: 'configName', label: 'm17.configEngine.colName' },
      { key: 'paymentMode', label: 'm17.configEngine.colMode' },
      { key: 'paymentLimit', label: 'm17.configEngine.colLimit' },
    ],
    fields: [
      { name: 'configCode', label: 'm17.configEngine.fPaymentConfigCode', type: 'text', required: true },
      { name: 'configName', label: 'm17.configEngine.fPaymentConfigName', type: 'text', required: true },
      { name: 'paymentMode', label: 'm17.configEngine.fPaymentMode', type: 'text', required: true },
      { name: 'paymentLimit', label: 'm17.configEngine.fPaymentLimit', type: 'number' },
    ],
    build: (v) => ({
      configCode: v.configCode,
      configName: v.configName,
      paymentMode: v.paymentMode,
      ...(v.paymentLimit ? { paymentLimit: Number(v.paymentLimit) } : {}),
    }),
  },
  {
    key: 'notification-configs',
    label: 'm17.configEngine.domNotification',
    path: 'notification-configs',
    idField: 'configId',
    columns: [
      { key: 'configCode', label: 'm17.configEngine.colCode' },
      { key: 'configName', label: 'm17.configEngine.colName' },
      { key: 'channelType', label: 'm17.configEngine.colChannel' },
      { key: 'triggerEvent', label: 'm17.configEngine.colTrigger' },
    ],
    fields: [
      { name: 'configCode', label: 'm17.configEngine.fConfigCode', type: 'text', required: true },
      { name: 'configName', label: 'm17.configEngine.fConfigName', type: 'text', required: true },
      { name: 'channelType', label: 'm17.configEngine.fChannelType', type: 'text', required: true },
      { name: 'templateCode', label: 'm17.configEngine.fTemplateCode', type: 'text', required: true },
      { name: 'templateBody', label: 'm17.configEngine.fTemplateBody', type: 'text', required: true },
      { name: 'triggerEvent', label: 'm17.configEngine.fTriggerEvent', type: 'text', required: true },
    ],
    build: (v) => ({
      configCode: v.configCode,
      configName: v.configName,
      channelType: v.channelType,
      templateCode: v.templateCode,
      templateBody: v.templateBody,
      triggerEvent: v.triggerEvent,
    }),
  },
  {
    key: 'parameters',
    label: 'm17.configEngine.domParameter',
    path: 'parameters',
    idField: 'parameterId',
    columns: [
      { key: 'parameterCode', label: 'm17.configEngine.colCode' },
      { key: 'parameterName', label: 'm17.configEngine.colName' },
      { key: 'parameterValue', label: 'm17.configEngine.colValue' },
      { key: 'category', label: 'm17.configEngine.colCategory' },
    ],
    fields: [
      { name: 'parameterCode', label: 'm17.configEngine.fParameterCode', type: 'text', required: true },
      { name: 'parameterName', label: 'm17.configEngine.fParameterName', type: 'text', required: true },
      { name: 'parameterValue', label: 'm17.configEngine.fParameterValue', type: 'text', required: true },
      { name: 'category', label: 'm17.configEngine.fCategory', type: 'text' },
      { name: 'effectiveFromDate', label: 'm17.configEngine.fEffectiveFrom', type: 'date', required: true },
    ],
    build: (v) => ({
      parameterCode: v.parameterCode,
      parameterName: v.parameterName,
      parameterValue: v.parameterValue,
      ...(v.category ? { category: v.category } : {}),
      effectiveFromDate: v.effectiveFromDate || TODAY(),
    }),
  },
];

type Row = Record<string, unknown>;

export function M17ConfigEnginePage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [domainKey, setDomainKey] = useState<string>(DOMAINS[0].key);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const domain = DOMAINS.find((d) => d.key === domainKey) ?? DOMAINS[0];

  useEffect(() => {
    let alive = true;
    setRows(null);
    setError(null);
    apiRequest<{ data: Row[] }>(`/v1/m17/engine/${domain.path}?page=1&limit=50`, { token })
      .then((r) => { if (alive) setRows(r.data ?? []); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, domain.path, reloadKey]);

  function onSelectDomain(key: string): void {
    setDomainKey(key);
    setValues({});
    setNotice(null);
  }

  async function onCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setNotice(null);
    setError(null);
    for (const f of domain.fields) {
      if (f.required && !values[f.name]) { setError(t('m17.configEngine.fieldRequired', { label: t(f.label) })); return; }
    }
    setSaving(true);
    try {
      await apiRequest(`/v1/m17/engine/${domain.path}`, { method: 'POST', token, body: domain.build(values) });
      setNotice(t('m17.configEngine.created', { label: t(domain.label) }));
      setValues({});
      setReloadKey((k) => k + 1);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function onDeactivate(id: string): Promise<void> {
    setNotice(null);
    setError(null);
    try {
      await apiRequest(`/v1/m17/engine/${domain.path}/${id}`, { method: 'DELETE', token });
      setNotice(t('m17.configEngine.deactivated'));
      setReloadKey((k) => k + 1);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <main aria-label="config-engine">
      <h1>{t('m17.configEngine.title')}</h1>

      <label htmlFor="ce-domain">{t('m17.configEngine.domainLabel')}</label>
      <select id="ce-domain" aria-label="engine-domain" value={domainKey} onChange={(e) => onSelectDomain(e.target.value)}>
        {DOMAINS.map((d) => <option key={d.key} value={d.key}>{t(d.label)}</option>)}
      </select>

      <form onSubmit={onCreate} aria-label="engine-create-form" noValidate onSubmitCapture={validateDateSubmission}>
        <h2>{t('m17.configEngine.newDomain', { label: t(domain.label) })}</h2>
        {domain.fields.map((f) => (
          <span key={f.name}>
            <label htmlFor={`ce-${f.name}`}>{t(f.label)}</label>
            {f.type === 'date' ? <DateInput
              id={`ce-${f.name}`}
              value={values[f.name] ?? ''}
              onValueChange={(nextValue) => setValues((v) => ({ ...v, [f.name]: nextValue }))}
            /> : <input
              id={`ce-${f.name}`}
              type={f.type === 'number' ? 'number' : 'text'}
              value={values[f.name] ?? ''}
              onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
            />}
          </span>
        ))}
        <button type="submit" disabled={saving}>{t('m17.configEngine.createBtn')}</button>
      </form>

      {notice ? <p role="status">{notice}</p> : null}
      {error ? <p role="alert">{error}</p> : null}

      {!rows ? <p>{t('m17.configEngine.loading')}</p> : (
        <table aria-label="engine-table">
          <thead>
            <tr>{domain.columns.map((c) => <th key={c.key}>{t(c.label)}</th>)}<th>{t('m17.configEngine.colActions')}</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const id = String(r[domain.idField] ?? i);
              return (
                <tr key={id}>
                  {domain.columns.map((c) => <td key={c.key}>{r[c.key] == null ? '—' : String(r[c.key])}</td>)}
                  <td>
                    <button type="button" aria-label={`deactivate-${id}`} onClick={() => onDeactivate(id)}>{t('m17.configEngine.deactivateBtn')}</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </main>
  );
}
